import React from 'react';

interface NetworkConfigViewProps {
  params: Record<string, unknown>;
}

function renderValue(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

/**
 * Read-only view of a model's network config.
 *
 * One row per param, with a single level of nesting expanded — the shapes that actually occur
 * (`thresholds`, `inference_params`) are flat maps, and expanding them beats making the reader
 * parse a wall of JSON. Anything deeper falls back to its JSON form on one line.
 */
export const NetworkConfigView: React.FC<NetworkConfigViewProps> = ({ params }) => {
  const entries = Object.entries(params ?? {});
  if (entries.length === 0) {
    return (
      <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
        No network config recorded for this model.
      </div>
    );
  }

  const isPlainObject = (v: unknown): v is Record<string, unknown> =>
    typeof v === 'object' && v !== null && !Array.isArray(v);

  return (
    <table className="admin-table" style={{ marginBottom: 0 }}>
      <tbody>
        {entries.map(([key, value]) => {
          const nested = isPlainObject(value) ? Object.entries(value) : null;
          if (nested && nested.length > 0) {
            return (
              <React.Fragment key={key}>
                <tr>
                  <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 600 }}>
                    {key}
                  </td>
                  <td />
                </tr>
                {nested.map(([nestedKey, nestedValue]) => (
                  <tr key={`${key}.${nestedKey}`}>
                    <td
                      style={{
                        fontFamily: 'var(--font-mono)',
                        fontSize: 12,
                        color: 'var(--text-secondary)',
                        paddingLeft: 28,
                      }}
                    >
                      {nestedKey}
                    </td>
                    <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>
                      {renderValue(nestedValue)}
                    </td>
                  </tr>
                ))}
              </React.Fragment>
            );
          }
          return (
            <tr key={key}>
              <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 600 }}>
                {key}
              </td>
              <td
                style={{ fontFamily: 'var(--font-mono)', fontSize: 12, wordBreak: 'break-all' }}
              >
                {renderValue(value)}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};
